import { ActivityEntityName } from '../../../../entities/shared/activity.entity.name';
import { MergeDataset } from '../../decorators/decorators/merge.dataset';
import { MergeSource } from '../../decorators/decorators/merge.source';
import { AccountContactMergeData, AccountContactMergeSchema } from '../data/account.contact.merge.data';
import { AccountMergeData, AccountMergeSchema } from '../data/account.merge.data';
import { AttachmentsMergeData, AttachmentsMergeSchema } from '../data/activity.merge.data';
import { CompanyMergeData, CompanyMergeSchema } from '../data/company.merge.data';
import { ContactFullMergeData, ContactFullMergeSchema } from '../data/contact.full.merge.data';
import { ContactMergeSchema } from '../data/contact.merge.data';
import { EmployeeMergeData, EmployeeMergeSchema } from '../data/employee.merge.data';
import { JobAssetsMergeData, JobAssetsMergeSchema } from '../data/job.assets.merge.data';
import { JobContactLinksMergeData, JobContactLinksMergeSchema } from '../data/job.contact.link.merge.data';
import { JobIncludeItemsMergeData, JobIncludeItemsMergeDataSchema } from '../data/job.include.items.merge.data';
import { JobMergeData, JobMergeSchema } from '../data/job.merge.data';
import { JobCustomerAddressMergeData, JobCustomerAddressMergeSchema } from '../data/job.customer.address.merge.data';
import { JobCustomerContactMergeData, JobCustomerContactMergeSchema } from '../data/job.customer.contact.merge.data';
import { JobSiteMergeData, JobSiteMergeSchema } from '../data/job.site.merge.data';
import { JobStagesMergeData, JobStagesMergeDataSchema } from '../data/job.stages.merge.data';
import { OtherMergeData, OtherMergeDataSchema } from '../data/other.merge.data';
import { TaskArrayMergeData, TaskArrayMergeDataSchema } from '../data/task.merge.data';
import { TimesheetArrayMergeData, TimesheetArrayMergeDataSchema } from '../data/timesheet.merge.data';
import { EquipmentArrayMergeData, EquipmentArrayMergeDataSchema } from '../data/equipment.merge.data';
import { JobPrimaryContactMergeData, JobPrimaryContactMergeSchema } from '../data/job.primary.contact.merge.data';

@MergeDataset({
    entity: ActivityEntityName.Job
})
export class JobMergeDataset {

    @MergeSource({
        schema: JobMergeSchema
    })
    job: JobMergeData;

    @MergeSource({
        schema: JobStagesMergeDataSchema
    })
    jobStage: JobStagesMergeData;

    @MergeSource({
        schema: AccountMergeSchema
    })
    jobCustomer: AccountMergeData;

    // The customer's address and contact as held on the job. Labelled for the UI's vocabulary
    // ("address", not "site") — the token prefix keeps the dataset's `job` prefix because
    // QuoteProJobMergeDataset pulls these sources into its own namespace and de-dupes by name.
    @MergeSource({
        schema: JobCustomerAddressMergeSchema,
        displayName: 'Customer Address',
        // Site custom fields resolve off a CRM site record, which a hand-entered customer address
        // does not have — publishing them here would offer tokens that render empty.
        ignoreCustomFields: true,
    })
    jobCustomerAddress: JobCustomerAddressMergeData;

    @MergeSource({
        schema: JobCustomerContactMergeSchema,
        displayName: 'Customer Contact',
        ignoreCustomFields: true,
    })
    jobCustomerAddressContact: JobCustomerContactMergeData;

    // Resolved live from the customer record at merge time, so its values can change under an
    // already-issued document. The label carries the source, to separate it from the job-held
    // "Customer Contact" above; the token prefix is unchanged, so stored templates are unaffected.
    @MergeSource({
        schema: AccountContactMergeSchema,
        displayName: 'Customer Primary Contact (CRM)'
    })
    jobCustomerContact: AccountContactMergeData;

    @MergeSource({
        schema: JobPrimaryContactMergeSchema,
        ignoreCustomFields: true,
    })
    jobPrimaryContact: JobPrimaryContactMergeData;

    @MergeSource({
        schema: JobSiteMergeSchema
    })
    jobSite: JobSiteMergeData;

    @MergeSource({
        schema: CompanyMergeSchema
    })
    jobBillingCompany: CompanyMergeData;

    @MergeSource({
        schema: EmployeeMergeSchema
    })
    jobFieldSupervisor: EmployeeMergeData;

    @MergeSource({
        schema: EmployeeMergeSchema
    })
    jobOwner: EmployeeMergeData;

    @MergeSource({
        schema: JobContactLinksMergeSchema,
        displayName: 'Job Contacts'
    })
    jobContact: JobContactLinksMergeData;

    @MergeSource({
        schema: JobAssetsMergeSchema,
        ignoreCustomFields: true,
    })
    jobAsset: JobAssetsMergeData;

    @MergeSource({
        schema: JobIncludeItemsMergeDataSchema,
        ignoreCustomFields: true,
    })
    jobItems: JobIncludeItemsMergeData;

    @MergeSource({
        schema: OtherMergeDataSchema,
    })
    other: OtherMergeData;

    @MergeSource({
        schema: AttachmentsMergeSchema,
        ignoreCustomFields: true,
    })
    files?: AttachmentsMergeData;

    @MergeSource({
        schema: TaskArrayMergeDataSchema,
        ignoreCustomFields: true,
    })
    jobTasks: TaskArrayMergeData;

    @MergeSource({
        schema: TimesheetArrayMergeDataSchema,
        ignoreCustomFields: true,
    })
    jobTimesheets: TimesheetArrayMergeData;

    @MergeSource({
        schema: EquipmentArrayMergeDataSchema,
        ignoreCustomFields: true,
    })
    jobEquipments: EquipmentArrayMergeData;


}